/**
 * AddAssetButton — Sprint 18.5 reusable search+add modal.
 * Searches CoinGecko (crypto) or Finnhub (stocks) and adds on selection.
 *
 * Props:
 *   asset_type: 'crypto' | 'stock'
 *   default_tier: string (e.g. 'tracked' for crypto, 'monitor' for stocks)
 *   on_added: callback fired after successful add
 *   label: optional button label (default "+ Add Crypto" or "+ Add Ticker")
 */

import { useState, useEffect, useRef } from 'react';
import { Plus, Search, X } from 'lucide-react';

const CRYPTO_TIERS = [
  { value: 'dca_target', label: 'DCA Target (core accumulation)' },
  { value: 'tracked', label: 'Tracked (monitor)' },
  { value: 'opportunistic', label: 'Opportunistic (small-cap call)' },
];

const STOCK_TIERS = [
  { value: 'core', label: 'Core (long-term hold)' },
  { value: 'active', label: 'Active (actively traded)' },
  { value: 'monitor', label: 'Monitor (watch only)' },
];

export default function AddAssetButton({ asset_type = 'crypto', default_tier, on_added, label }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [tier, setTier] = useState(default_tier || (asset_type === 'crypto' ? 'tracked' : 'monitor'));
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const tierOptions = asset_type === 'crypto' ? CRYPTO_TIERS : STOCK_TIERS;
  const buttonLabel = label || (asset_type === 'crypto' ? '+ Add Crypto' : '+ Add Ticker');

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open || !query || query.length < 2) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const endpoint = asset_type === 'crypto' ? `/api/crypto/search?q=${encodeURIComponent(query)}` : `/api/watchlist/search?q=${encodeURIComponent(query)}`;
        const r = await fetch(endpoint);
        if (r.ok) {
          const data = await r.json();
          setResults(data.coins || data.results || []);
        }
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open, asset_type]);

  async function add() {
    if (!selectedCoin) return;
    setAdding(true);
    setError(null);
    try {
      const endpoint = asset_type === 'crypto' ? '/api/crypto/assets' : '/api/watchlist/ticker';
      const body = asset_type === 'crypto'
        ? { symbol: selectedCoin.symbol, name: selectedCoin.name, coingecko_id: selectedCoin.coingecko_id, tier, category: tier === 'dca_target' ? 'L1' : null }
        : { ticker: selectedCoin.symbol, name: selectedCoin.name, tier };
      const r = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setError(err.error || `Failed: ${r.status}`);
        setAdding(false);
        return;
      }
      setOpen(false); setQuery(''); setSelectedCoin(null); setResults([]);
      setAdding(false);
      on_added?.();
    } catch (e) { setError(e.message); setAdding(false); }
  }

  function close() {
    setOpen(false); setQuery(''); setSelectedCoin(null); setResults([]); setError(null);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 text-[10px] font-mono px-3 py-1.5 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20"
      >
        <Plus className="w-3 h-3" /> {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
          <div onClick={e => e.stopPropagation()} className="bg-korinth-surface border border-korinth-border rounded-lg max-w-xl w-full mt-16">
            <div className="p-4 border-b border-korinth-border flex items-center justify-between">
              <span className="text-sm font-mono font-bold text-white">{buttonLabel.replace('+', '').trim()}</span>
              <button onClick={close} className="text-korinth-muted hover:text-white"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-4 space-y-3">
              {/* Search input */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-korinth-muted absolute left-2.5 top-2.5" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={e => { setQuery(e.target.value); setSelectedCoin(null); }}
                  placeholder={asset_type === 'crypto' ? 'Search CoinGecko (e.g. dogecoin, pepe)' : 'Search ticker or company name'}
                  className="w-full bg-korinth-bg border border-korinth-border rounded pl-8 pr-3 py-2 text-xs font-mono text-white"
                />
                {loading && <span className="absolute right-3 top-2.5 text-[9px] text-korinth-muted">…</span>}
              </div>

              {/* Results */}
              {results.length > 0 && (
                <div className="space-y-0.5 max-h-64 overflow-y-auto">
                  {results.map(r => (
                    <button
                      key={r.coingecko_id || r.symbol}
                      onClick={() => setSelectedCoin(r)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-left text-[11px] font-mono transition-colors ${selectedCoin?.symbol === r.symbol ? 'bg-korinth-accent/10 border border-korinth-accent' : 'bg-korinth-bg hover:bg-korinth-border/30 border border-transparent'}`}
                    >
                      {r.thumb && <img src={r.thumb} alt="" className="w-5 h-5 rounded-full" />}
                      <span className="text-white font-bold w-16">{r.symbol}</span>
                      <span className="text-korinth-text flex-1 truncate">{r.name}</span>
                      {r.market_cap_rank && <span className="text-[9px] text-korinth-muted">#{r.market_cap_rank}</span>}
                    </button>
                  ))}
                </div>
              )}

              {query.length >= 2 && !loading && results.length === 0 && (
                <div className="text-[10px] text-korinth-muted font-mono text-center py-3">No results for "{query}"</div>
              )}

              {/* Tier + Confirm */}
              {selectedCoin && (
                <div className="space-y-2 pt-2 border-t border-korinth-border/30">
                  <div>
                    <label className="text-[9px] font-mono text-korinth-muted">Add as:</label>
                    <select value={tier} onChange={e => setTier(e.target.value)}
                      className="block w-full mt-1 bg-korinth-bg border border-korinth-border rounded px-2 py-1.5 text-xs font-mono text-white">
                      {tierOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  {error && <div className="text-[10px] text-korinth-danger font-mono">{error}</div>}
                  <button onClick={add} disabled={adding}
                    className="w-full text-xs font-mono font-bold px-3 py-2 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20 disabled:opacity-50">
                    {adding ? 'Adding…' : `Add ${selectedCoin.symbol} to ${tier.replace(/_/g, ' ')}`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
